import { useEffect, useRef, useState } from "react";

export interface SearchItem {
  keys: string[];
  content: string;
  value?: string;
}

export type WithSearchScore<T> = T & {
  score?: number;
};

export interface SearchProps {
  items: string[] | SearchItem[];
  query: string;
}

export const getSearch = <
  T extends {
    keys: string[];
  },
>(
  items: T[],
  query: string,
): WithSearchScore<T>[] => {
  const copy = items.slice();
  const len = items.length;
  const key = query.toLowerCase();
  let i = -1;
  let c = 0;
  let tmp: {
    keys: string[];
  };
  const res = new Array(len);
  while (++i !== len) {
    tmp = copy[i];
    /**
     * Caluculate Minimum Index
     */
    let j = -1;
    let index = -1;
    const keylen = tmp.keys.length;
    let current;
    while (++j !== keylen) {
      current = tmp.keys[j].toLowerCase().indexOf(key);
      if (index === -1 || (current !== -1 && current < index)) {
        index = current;
      }
    }
    /**
     * Caluculate Score
     */
    const score = index > -1 ? 1 / (index + 1) : 0;
    if (score > 0) {
      res[c++] = { ...tmp, score };
    }
  }

  res.length = c;
  res.sort((a, b) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (a.score <= b.score) {
      return 1;
    } else {
      return -1;
    }
  });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return res;
};

export const useSearch = ({ items, query }: SearchProps) => {
  const cache = useRef<SearchItem[]>(
    typeof items[0] === "string"
      ? (items as string[]).map((item) => {
          return {
            keys: [item.toLowerCase()],
            content: item,
            value: item,
          };
        })
      : (items as {
          keys: string[];
          content: string;
        }[]),
  );
  const [result, setResult] = useState<WithSearchScore<SearchItem>[]>([]);

  useEffect(() => {
    setResult(getSearch(cache.current, query));
  }, [query]);

  return { result };
};
